import { useState, useEffect } from 'react';
import {
  Timer,
  Users,
  Activity,
  AlertTriangle,
  Siren,
  BarChart2,
  TrendingUp,
  Clock,
  Baby,
  Edit,
  Save,
  X,
  RotateCcw,
  Info,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '../../components/LocaleSelector';
import { useAdminLocale } from '../../hooks/useAdminLocale';

// ============================================
// TYPES
// ============================================

interface KPIStats {
  totalSessions: number;
  totalContractions: number;
  activeUsers: number;
  avgContractionsPerSession: number;
  hospitalRuleMet: number;
  emergencyEvents: number;
  avgSessionDuration: number;
  statsNotificationsSent: number;
}

interface NotificationTemplate {
  id: string;
  type: string;
  locale: string;
  title_template: string;
  message_template: string;
  available_placeholders: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

type TimeRange = '7d' | '30d' | '90d' | 'all';

// ============================================
// CONTRACTION NOTIFICATION TYPES
// ============================================

const contractionTypes = [
  'contraction_tracking_started',
  'contraction_hospital_rule_met',
  'contraction_emergency_event',
  'contraction_stats_update',
];

const typeLabels: Record<string, { label: string; icon: typeof Timer; color: string; description: string }> = {
  contraction_tracking_started: {
    label: 'Tracking gestartet',
    icon: Timer,
    color: 'text-blue-600 bg-blue-100',
    description: 'Wird an den Partner gesendet, wenn das Wehen-Tracking gestartet wird.',
  },
  contraction_hospital_rule_met: {
    label: 'Krankenhaus-Regel erreicht',
    icon: AlertTriangle,
    color: 'text-red-600 bg-red-100',
    description: 'Wird an den Partner gesendet, wenn die konfigurierte Regel (z.B. 5-1-1) erreicht wird.',
  },
  contraction_emergency_event: {
    label: 'Notfall-Ereignis',
    icon: Siren,
    color: 'text-red-600 bg-red-100',
    description: 'Wird bei Blasensprung oder Blutung an den Partner gesendet.',
  },
  contraction_stats_update: {
    label: 'Statistik-Update',
    icon: BarChart2,
    color: 'text-purple-600 bg-purple-100',
    description: 'Periodische Zusammenfassung basierend auf dem eingestellten Intervall.',
  },
};

const defaultTemplates: Record<string, { title: string; message: string }> = {
  contraction_tracking_started: {
    title: 'Wehen-Tracking gestartet',
    message: '{{partner_name}} hat das Wehen-Tracking gestartet.',
  },
  contraction_hospital_rule_met: {
    title: 'Krankenhaus-Regel erreicht!',
    message: '{{partner_name}} hat die {{rule}}-Regel erreicht. Es könnte Zeit sein, ins Krankenhaus zu fahren.',
  },
  contraction_emergency_event: {
    title: 'Notfall-Ereignis gemeldet!',
    message: '{{partner_name}} hat {{event_type}} gemeldet. Bitte kontaktiere sie/ihn sofort!',
  },
  contraction_stats_update: {
    title: 'Wehen-Update',
    message: '{{contraction_count}} Wehen in den letzten {{interval_minutes}} Min. \u2300 Länge: {{avg_duration}} | \u2300 Abstand: {{avg_interval}}',
  },
};

// ============================================
// KPI CARD COMPONENT
// ============================================

interface KPICardProps {
  title: string;
  value: string | number;
  icon: typeof Timer;
  color: string;
  trend?: number;
  subtitle?: string;
}

function KPICard({ title, value, icon: Icon, color, trend, subtitle }: KPICardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span>{Math.abs(trend)}% vs. Vorperiode</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// ============================================
// TEMPLATE EDITOR COMPONENT
// ============================================

interface TemplateEditorProps {
  template: NotificationTemplate;
  onSave: (id: string, title: string, message: string) => Promise<void>;
  onReset: (id: string) => Promise<void>;
  onToggle: (id: string, active: boolean) => Promise<void>;
}

function TemplateEditor({ template, onSave, onReset, onToggle }: TemplateEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(template.title_template);
  const [editMessage, setEditMessage] = useState(template.message_template);
  const [isSaving, setIsSaving] = useState(false);

  const typeInfo = typeLabels[template.type] || {
    label: template.type,
    icon: Timer,
    color: 'text-gray-600 bg-gray-100',
    description: '',
  };
  const Icon = typeInfo.icon;

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(template.id, editTitle, editMessage);
    setIsSaving(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(template.title_template);
    setEditMessage(template.message_template);
    setIsEditing(false);
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById(`message-${template.id}`) as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editMessage.substring(0, start) + `{{${placeholder}}}` + editMessage.substring(end);
      setEditMessage(newValue);
      // Focus and set cursor position after the inserted placeholder
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + placeholder.length + 4;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else {
      setEditMessage(editMessage + `{{${placeholder}}}`);
    }
  };

  return (
    <div className="p-5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`p-2.5 rounded-full ${typeInfo.color}`}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900">{typeInfo.label}</h3>
            {!template.is_active && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                Deaktiviert
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">{typeInfo.description}</p>

          {isEditing ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Titel</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nachricht</label>
                <textarea
                  id={`message-${template.id}`}
                  value={editMessage}
                  onChange={(e) => setEditMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Placeholder Buttons */}
              {template.available_placeholders.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">
                    Variablen einfügen:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {template.available_placeholders.map((p) => (
                      <button
                        key={p}
                        onClick={() => insertPlaceholder(p)}
                        className="px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
                      >
                        {`{{${p}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 inline mr-1" />
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4 inline mr-1" />
                  Speichern
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-gray-800">{template.title_template}</p>
              <p className="text-sm text-gray-500 mt-1">{template.message_template}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              title="Bearbeiten"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onReset(template.id)}
              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
              title="Auf Standard zurücksetzen"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggle(template.id, !template.is_active)}
              className={`p-2 rounded-lg transition-colors ${
                template.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
              }`}
              title={template.is_active ? 'Deaktivieren' : 'Aktivieren'}
            >
              <div
                className={`w-8 h-5 rounded-full transition-colors ${
                  template.is_active ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                    template.is_active ? 'translate-x-3.5' : 'translate-x-0.5'
                  } mt-0.5`}
                />
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export function ContractionTimerAdminPage() {
  const { contentLocale } = useAdminLocale();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [stats, setStats] = useState<KPIStats | null>(null);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  // Fetch KPI stats
  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  // Fetch templates
  useEffect(() => {
    fetchTemplates();
  }, [contentLocale]);

  const getDateFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return null;
    }
  };

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const supabase = getSupabaseAdminClient();
      const dateFilter = getDateFilter();

      // Build base query for contractions
      let contractionsQuery = supabase.from('contractions').select('id, pregnancy_id, duration_seconds', { count: 'exact' });
      if (dateFilter) {
        contractionsQuery = contractionsQuery.gte('created_at', dateFilter);
      }
      const { data: contractionsData, count: totalContractions } = await contractionsQuery;
      const contractions = contractionsData as Array<{ id: string; pregnancy_id: string; duration_seconds: number | null }> | null;

      // Get unique users/sessions
      const uniquePregnancies = new Set(contractions?.map((c) => c.pregnancy_id) || []);
      const totalSessions = uniquePregnancies.size;

      // Get active users (with contractions)
      const activeUsersQuery = supabase.from('contraction_settings').select('user_id', { count: 'exact' });
      const { count: activeUsers } = await activeUsersQuery;

      // Get emergency events count
      let emergencyQuery = supabase.from('contraction_emergency_events').select('id', { count: 'exact' });
      if (dateFilter) {
        emergencyQuery = emergencyQuery.gte('created_at', dateFilter);
      }
      const { count: emergencyEvents } = await emergencyQuery;

      // Get hospital rule notifications
      let hospitalRuleQuery = supabase
        .from('notifications')
        .select('id', { count: 'exact' })
        .eq('type', 'contraction_hospital_rule_met');
      if (dateFilter) {
        hospitalRuleQuery = hospitalRuleQuery.gte('created_at', dateFilter);
      }
      const { count: hospitalRuleMet } = await hospitalRuleQuery;

      // Get stats notifications sent
      let statsNotifQuery = supabase
        .from('notifications')
        .select('id', { count: 'exact' })
        .eq('type', 'contraction_stats_update');
      if (dateFilter) {
        statsNotifQuery = statsNotifQuery.gte('created_at', dateFilter);
      }
      const { count: statsNotificationsSent } = await statsNotifQuery;

      // Calculate averages
      const avgContractionsPerSession = totalSessions > 0 ? Math.round((totalContractions || 0) / totalSessions) : 0;
      const totalDuration = contractions?.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) || 0;
      const avgSessionDuration = totalContractions ? Math.round(totalDuration / (totalContractions || 1)) : 0;

      setStats({
        totalSessions,
        totalContractions: totalContractions || 0,
        activeUsers: activeUsers || 0,
        avgContractionsPerSession,
        hospitalRuleMet: hospitalRuleMet || 0,
        emergencyEvents: emergencyEvents || 0,
        avgSessionDuration,
        statsNotificationsSent: statsNotificationsSent || 0,
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const { data, error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .select('*')
        .in('type', contractionTypes)
        .eq('locale', contentLocale)
        .order('type');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleSaveTemplate = async (id: string, title: string, message: string) => {
    if (!title.trim() || !message.trim()) {
      alert('Titel und Nachricht dürfen nicht leer sein');
      return;
    }

    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          title_template: title,
          message_template: message,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) => (t.id === id ? { ...t, title_template: title, message_template: message } : t))
      );
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Fehler beim Speichern');
    }
  };

  const handleResetTemplate = async (id: string) => {
    const template = templates.find((t) => t.id === id);
    if (!template) return;

    const defaultText = defaultTemplates[template.type];
    if (!defaultText && contentLocale === 'de') {
      alert('Kein Standardtext für diesen Typ verfügbar');
      return;
    }

    if (!confirm(`Möchtest du den Text für "${typeLabels[template.type]?.label || template.type}" auf den Standardwert zurücksetzen?`)) {
      return;
    }

    // For non-DE locales, reset to empty strings (no German defaults)
    const resetTitle = contentLocale === 'de' ? (defaultText?.title ?? '') : '';
    const resetMessage = contentLocale === 'de' ? (defaultText?.message ?? '') : '';

    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          title_template: resetTitle,
          message_template: resetMessage,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', id);

      if (error) throw error;

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, title_template: resetTitle, message_template: resetMessage } : t
        )
      );
    } catch (err) {
      console.error('Error resetting template:', err);
      alert('Fehler beim Zurücksetzen');
    }
  };

  const handleToggleTemplate = async (id: string, active: boolean) => {
    try {
      const { error } = await getSupabaseAdminClient()
        .from('notification_templates')
        .update({
          is_active: active,
          updated_at: new Date().toISOString(),
        } as never)
        .eq('id', id);

      if (error) throw error;

      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, is_active: active } : t)));
    } catch (err) {
      console.error('Error toggling template:', err);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wehenzähler</h1>
          <p className="text-gray-500 mt-1">Statistiken und Benachrichtigungsvorlagen verwalten</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
          {(['7d', '30d', '90d', 'all'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range === '7d' && '7 Tage'}
              {range === '30d' && '30 Tage'}
              {range === '90d' && '90 Tage'}
              {range === 'all' && 'Gesamt'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      {isLoadingStats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tracking-Sessions"
            value={stats.totalSessions}
            icon={Timer}
            color="bg-blue-100 text-blue-600"
            subtitle="Einzigartige Schwangerschaften"
          />
          <KPICard
            title="Wehen erfasst"
            value={stats.totalContractions}
            icon={Activity}
            color="bg-green-100 text-green-600"
          />
          <KPICard
            title="Aktive Nutzer"
            value={stats.activeUsers}
            icon={Users}
            color="bg-purple-100 text-purple-600"
            subtitle="Mit Einstellungen"
          />
          <KPICard
            title="Ø Wehen/Session"
            value={stats.avgContractionsPerSession}
            icon={BarChart2}
            color="bg-teal-100 text-teal-600"
          />
          <KPICard
            title="Krankenhaus-Regel"
            value={stats.hospitalRuleMet}
            icon={AlertTriangle}
            color="bg-amber-100 text-amber-600"
            subtitle="Benachrichtigungen"
          />
          <KPICard
            title="Notfall-Events"
            value={stats.emergencyEvents}
            icon={Siren}
            color="bg-red-100 text-red-600"
            subtitle="Blasensprung / Blutung"
          />
          <KPICard
            title="Ø Wehen-Dauer"
            value={formatDuration(stats.avgSessionDuration)}
            icon={Clock}
            color="bg-indigo-100 text-indigo-600"
          />
          <KPICard
            title="Stats-Updates"
            value={stats.statsNotificationsSent}
            icon={BarChart2}
            color="bg-cyan-100 text-cyan-600"
            subtitle="Benachrichtigungen"
          />
        </div>
      ) : null}

      {/* Notification Templates */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Benachrichtigungs-Vorlagen</h2>
            <p className="text-sm text-gray-500 mt-1">
              Passe die Texte für Wehenzähler-Benachrichtigungen an
            </p>
          </div>
          <LocaleSelector />
        </div>

        {/* Info Banner */}
        <div className="mx-5 mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Variablen</p>
            <p className="mt-1">
              Klicke auf die Variablen-Buttons beim Bearbeiten, um Platzhalter einzufügen. Diese werden automatisch durch die echten Werte ersetzt.
            </p>
          </div>
        </div>

        {/* Templates List */}
        {isLoadingTemplates ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-teal-200 border-t-teal-600 rounded-full animate-spin mx-auto" />
          </div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Baby className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Keine Vorlagen gefunden.</p>
            <p className="text-sm mt-1">Die Vorlagen werden automatisch erstellt, wenn die Migration ausgeführt wird.</p>
          </div>
        ) : (
          <div className="mt-4">
            {templates.map((template) => (
              <TemplateEditor
                key={template.id}
                template={template}
                onSave={handleSaveTemplate}
                onReset={handleResetTemplate}
                onToggle={handleToggleTemplate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
